import React, { useEffect } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { GraduationCapIcon, TrendingUpIcon, UsersIcon, MonitorIcon } from 'lucide-react';
import { DEFAULT_HIGHLIGHTS, EDUCATION_CITATIONS, getDefaultExploreBridge, getExploreBridge } from '../data/exploreBridges';
import { resolveProgramCodeSlug } from '../data/programBridges';
import { useMetaTags } from '../hooks/useMetaTags';
import { buildClicksSearchUrl } from '../utils/clicksSearchUrl';
import { BrandedExploreBridge } from '../components/BrandedExploreBridge';
import { getUniversityBySlug } from '../data/universityData';

// Ad-copy bridge template served at /explore/{brand}-{version}
// Icons paired positionally with the outcome stats: earnings, graduates,
// online enrollment.
const HIGHLIGHT_ICONS = [TrendingUpIcon, UsersIcon, MonitorIcon];
// Brand-neutral ad copy used when an /explore/ path matches no template.
const EXPLORE_FALLBACK_SLUG = 'search-v1';
// (e.g. /explore/UAGC-v1). Layout is fixed across every template:
// site nav → hero call to action → short paragraph section → footer.
// The copy, imagery, and outbound destination come from data/exploreBridges.
export function ExploreBridgePage() {
  const {
    bridgeSlug
  } = useParams<{
    bridgeSlug: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Fall back to the path segment so statically-registered routes (which
  // have no :bridgeSlug param) resolve the same as dynamic ones.
  const slug = bridgeSlug || location.pathname.replace(/\/+$/, '').split('/').filter(Boolean).pop();
  // Previewing the dynamic route directly (path shown as "/explore/:bridgeSlug")
  // has no real slug, so show the first template rather than a dead end.
  const isPlaceholderSlug = !slug || slug.startsWith(':');
  const bridge = isPlaceholderSlug ? getDefaultExploreBridge() : getExploreBridge(slug);
  // /explore/{degreeInterest}-{mProgram} (e.g. /explore/3-101) is shorthand
  // for the named page — redirect so the readable URL is what's shared and
  // reported on. Any query string carries over.
  const codeSlug = isPlaceholderSlug ? undefined : resolveProgramCodeSlug(slug);
  // An /explore/ path that matches nothing falls back to the brand-neutral
  // search page rather than dead-ending the visitor.
  const needsFallback = !bridge && !codeSlug;
  useEffect(() => {
    if (codeSlug) {
      navigate(`/explore/${codeSlug}${location.search}`, {
        replace: true
      });
      return;
    }
    if (needsFallback) {
      navigate(`/explore/${EXPLORE_FALLBACK_SLUG}${location.search}`, {
        replace: true
      });
    }
  }, [codeSlug, needsFallback, navigate, location.search]);
  useMetaTags({
    title: bridge?.metaTitle || 'Explore Online Degrees | FindOnlineDegrees',
    description: bridge?.metaDescription || 'Explore accredited online degree programs from our partner schools.',
    ogImage: bridge?.heroImage,
    // Ad-copy pages shouldn't compete with organic pages in search.
    robots: 'noindex, follow'
  });
  const handleNavigate = (page: string) => {
    navigate(page === 'compare' ? '/' : `/${page}`);
    window.scrollTo(0, 0);
  };
  // Carry any lead data and tracking params on this page's URL through to
  // the destination so the click can be attributed and the user isn't
  // asked for information they already provided.
  const handleCta = () => {
    if (!bridge) return;
    if (bridge.clicksSearch) {
      const url = buildClicksSearchUrl(location.search, bridge.clicksSearch);
      console.log('[Explore Bridge] redirecting to:', url);
      window.location.href = url;
      return;
    }
    if (!bridge.destinationUrl) return;
    const search = location.search.replace(/^\?/, '');
    const isExternal = /^https?:\/\//i.test(bridge.destinationUrl);
    const separator = bridge.destinationUrl.includes('?') ? '&' : '?';
    const destination = search ? `${bridge.destinationUrl}${separator}${search}` : bridge.destinationUrl;
    if (isExternal) {
      window.location.href = destination;
      return;
    }
    navigate(destination);
    window.scrollTo(0, 0);
  };
  // Branded ad copy renders the advertiser-styled layout instead.
  const university = bridge?.universitySlug ? getUniversityBySlug(bridge.universitySlug) : undefined;
  if (bridge && bridge.layout === 'branded' && university) {
    return <BrandedExploreBridge bridge={bridge} university={university} onCta={handleCta} data-id="element-1587" data-mp="gxj0g5d0q7uo" />;
  }
  // Numeric shorthand and unmatched slugs both redirect in the effect above;
  // render nothing for the frame in between rather than flashing a dead end.
  if (!bridge) return null;
  const highlights = bridge.highlights ?? DEFAULT_HIGHLIGHTS;
  return <div className="min-h-screen bg-white flex flex-col" data-id="element-1588" data-mp="2mt3w0x5085rl">
      <Header currentPage="explore" onNavigate={handleNavigate} data-id="element-1589" data-mp="2rqn93tpyuchy" />
      <main className="flex-grow" data-id="element-1590" data-mp="19rpl8enz5r44">
        {/* Hero + call to action */}
        <section className="relative border-b-4 border-[#e8594f]" data-id="element-1591" data-mp="19u1yieisfdau">
          <img src={bridge.heroImage} alt={bridge.heroImageAlt} className="absolute inset-0 w-full h-full object-cover" data-id="element-1592" data-mp="14smdunkiv8sz" />
          <div className={`absolute inset-0 ${bridge.heroHeadline ? 'bg-gray-900/55 lg:bg-gray-900/45' : 'bg-gray-900/40 lg:bg-gray-900/20'}`} aria-hidden="true" data-id="element-1593" data-mp="1er28v1mo727r" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-id="element-1594" data-mp="m3fucybwmd64">
            <div className="flex flex-col lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center" data-id="element-1595" data-mp="zukue1j6p7gz">
              {bridge.heroHeadline ?
            // Headline leads on every viewport: stacked above the call to
            // action on mobile, beside it on desktop.
            <h1 className="mb-8 lg:mb-0 text-center lg:text-left text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight" data-id="element-1596" data-mp="3c55sntniche">
                  {bridge.heroHeadline}
                </h1> : <div className="hidden lg:block" aria-hidden="true" data-id="element-1597" data-mp="27fhu2tizk2zg" />}
              <div className="w-full bg-white rounded-2xl shadow-2xl p-8 md:p-10 text-center max-w-xl mx-auto lg:mx-0" data-id="element-1598" data-mp="2c6q8k3z6aejh">
                {bridge.heroHeadline ? <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight" data-id="element-1599" data-mp="2h51dc0am1yuu">
                    {bridge.cardHeadline}
                  </h2> : <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight" data-id="element-1600" data-mp="1ohx5zoo1p8fw">
                    {bridge.cardHeadline}
                  </h1>}
                <p className="mt-4 text-lg font-semibold text-[#e8594f]" data-id="element-1601" data-mp="1yflctcyxe6ef">
                  {bridge.cardSubhead}
                </p>
                <button onClick={handleCta} className="mt-8 w-full sm:w-auto px-10 py-4 bg-[#e8594f] text-white text-lg font-bold rounded-lg shadow-md hover:bg-[#d54a41] hover:shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#e8594f]" data-id="element-1602" data-mp="34uvomdfa7fcz">
                  {bridge.ctaLabel}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* National education outcomes — compact band directly under the hero */}
        <section className="bg-gray-50 border-b border-gray-200" aria-label="Education outcomes" data-id="element-1603" data-mp="eiw2lhvpjvqw">
          <dl className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid gap-6 sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-gray-200" data-id="element-1604" data-mp="ahaqvzuwtnia">
            {highlights.map((item, i) => {
            const Icon = HIGHLIGHT_ICONS[i % HIGHLIGHT_ICONS.length];
            return <div key={item.label} className="flex gap-4 sm:px-6 sm:first:pl-0 sm:last:pr-0" data-id="element-1605" data-mp="1zw8ozchiiqg7" data-mpi={i}>
                  <Icon className="mt-1 h-5 w-5 flex-shrink-0 text-[#e8594f]" aria-hidden="true" data-id="element-1606" data-mp="1uy72siosib0i" />
                  <div data-id="element-1607" data-mp="18ro6hwbteeyc">
                    <dt className="text-xl font-extrabold text-gray-900 tracking-tight leading-none" data-id="element-1608" data-mp="18ptj8bd2mar3">
                      {item.stat}{' '}
                      <span className="text-sm font-semibold text-gray-500" data-id="element-1609" data-mp="u0e27n3cdjol">
                        {item.label}
                      </span>
                      {item.note && <sup className="ml-0.5 text-[10px] font-semibold text-gray-400" data-id="element-1610" data-mp="l3mzwsy758lk">
                          [{item.note}]
                        </sup>}
                    </dt>
                    <dd className="mt-2 text-sm text-gray-600 leading-relaxed" data-id="element-1611" data-mp="b5gx894kfyfn">
                      {item.body}
                    </dd>
                  </div>
                </div>;
          })}
          </dl>
        </section>

        {/* Short paragraph section */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20" data-id="element-1612" data-mp="2afp5iwrq0f2f">
          <div className="lg:grid lg:grid-cols-[1fr_360px] lg:gap-12 lg:items-start" data-id="element-1613" data-mp="3uzaoyf7gcuqh">
            <div data-id="element-1614" data-mp="3q0hc8ozzp7rd">
              <span className="mx-auto lg:mx-0 mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-[#e8594f]/10" aria-hidden="true" data-id="element-1615" data-mp="3q10wwdh0ya93">
                <GraduationCapIcon className="h-6 w-6 text-[#e8594f]" data-id="element-1616" data-mp="3rg6gp51vcvs" />
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center lg:text-left" data-id="element-1617" data-mp="hibfq2jgnvzw">
                {bridge.sectionHeading}
              </h2>
              <div className="mt-8 space-y-5" data-id="element-1618" data-mp="rhk84qyoh2k5">
                {bridge.paragraphs.map(paragraph => <p key={paragraph.slice(0, 40)} className="text-lg text-gray-600 leading-relaxed" data-id="element-1619" data-mp="mi7la67t3vhk">
                    {paragraph}
                  </p>)}
              </div>
            </div>

            <img src="https://cdn.magicpatterns.com/patterns/generated-images/c770ed7b-e4d2-4980-b486-3ba0bc2b040d.jpg" alt="Graduate in cap and gown holding her young daughter after her commencement ceremony" className="mt-10 lg:mt-0 w-full max-w-sm mx-auto lg:max-w-none rounded-2xl object-cover aspect-[4/5]" loading="lazy" data-id="element-1620" data-mp="2ocf92id53g4x" />
          </div>

          {/* Related pages — the same program at other levels, and sibling
              programs in the same field. Gives a visitor who is close but not
              committed somewhere to go other than back. */}
          {bridge.relatedGroups?.length ? <div className="mt-14 grid gap-10 sm:grid-cols-2" data-id="element-1621" data-mp="1n5faw40ti31j">
              {bridge.relatedGroups.map(group => <section key={group.heading} data-id="element-1622" data-mp="1j3ai17i71afy">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500" data-id="element-1623" data-mp="2fnj5vfg0fndi">
                    {group.heading}
                  </h3>
                  <ul className="mt-4 flex flex-wrap gap-2" data-id="element-1624" data-mp="2augd39j24hyd">
                    {group.items.map(item => <li key={item.to} data-id="element-1625" data-mp="3tlnfyty9wac6">
                        <Link to={item.to} className="inline-flex items-center rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-[#e8594f] hover:text-[#e8594f] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e8594f] focus-visible:ring-offset-2" data-id="element-1626" data-mp="2e19yuo2zvs5">
                          {item.label}
                        </Link>
                      </li>)}
                  </ul>
                </section>)}
            </div> : null}

          <div className="mt-14 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between rounded-2xl bg-gray-50 border border-gray-200 p-6 sm:p-8" data-id="element-1627" data-mp="u2w3op7bicwr">
            <p className="text-lg font-semibold text-gray-900 max-w-md" data-id="element-1628" data-mp="141k6igqqelfv">
              {bridge.closingLine || bridge.cardSubhead}
            </p>
            <button onClick={handleCta} className="w-full sm:w-auto flex-shrink-0 px-10 py-4 bg-[#e8594f] text-white text-lg font-bold rounded-lg shadow-md hover:bg-[#d54a41] hover:shadow-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#e8594f]" data-id="element-1629" data-mp="305rp85f2t66c">
              {bridge.ctaLabel}
            </button>
          </div>

          {bridge.disclosure && <p className="mt-10 text-xs text-gray-400 leading-relaxed" data-id="element-1630" data-mp="3eue9mljk8o4f">
              {bridge.disclosure}
            </p>}
        </section>

        {/* Citations for the national statistics above */}
        <section className="border-t border-gray-200 bg-white" aria-label="Citations" data-id="element-1631" data-mp="3nukmc57fpk2j">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-id="element-1632" data-mp="1yfcuctwo1ptn">
            <h2 className="text-sm font-bold text-gray-600 mb-3" data-id="element-1633" data-mp="1thba45ni7kgl">Citations</h2>
            <ol className="space-y-1.5" data-id="element-1634" data-mp="1tjea9iidetz1">
              {EDUCATION_CITATIONS.links.map((href, i) => <li key={href} className="text-xs text-gray-500" data-id="element-1635" data-mp="91d84nzy00qw" data-mpi={i}>
                  <span className="mr-1 text-gray-400" data-id="element-1636" data-mp="dxkfml7zlkay">[{i + 1}]</span>
                  <a href={href} target="_blank" rel="noopener noreferrer nofollow" className="underline break-all hover:text-gray-700" data-id="element-1637" data-mp="3vfd99ha7pfpo">
                    {href}
                  </a>
                </li>)}
            </ol>
            <div className="mt-5 space-y-1" data-id="element-1638" data-mp="3rebcd73rpfqv">
              {EDUCATION_CITATIONS.notes.map(note => <p key={note} className="text-xs text-gray-500 leading-relaxed" data-id="element-1639" data-mp="3chu1si3ek55d">
                  {note}
                </p>)}
            </div>
          </div>
        </section>
      </main>
      <Footer onNavigate={handleNavigate} data-id="element-1640" data-mp="1am9tm0ghtsrn" />
    </div>;
}